import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { Reorder, useDragControls } from 'framer-motion';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { canAccessTenantPath } from '@/shared/lib/navigation';
import { useSelection } from '@/shared/lib/use-selection';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { FloatingActionBar } from '@/shared/components/floating-action-bar';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { Pagination } from '@/shared/ui/pagination';
import type { FabButton } from '@/shared/types/fab-button';
import { isPersistedListId, normalizeOrderedListItems } from '@/shared/lib/ordered-list-items';
import {
  deleteFabButton,
  moveFabButtonOrder,
  reorderFabButton,
  toggleFabButtonActive,
  useFabButtonsFetcher,
} from '../api/use-fab-buttons';
import { FabButtonCard } from './fab-button-card';
import { FabButtonFormDialog } from './fab-button-form-dialog';

const PAGE_SIZE = 25;

interface DraggableFabItemProps {
  fab: FabButton;
  index: number;
  rowsLength: number;
  selected: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDragEnd: () => void;
  canPersist: boolean;
}

function DraggableFabItem({
  fab,
  index,
  rowsLength,
  selected,
  onToggle,
  onEdit,
  onDelete,
  onToggleActive,
  onMoveUp,
  onMoveDown,
  onDragEnd,
  canPersist,
}: DraggableFabItemProps) {
  const dragControls = useDragControls();
  return (
    <Reorder.Item
      value={fab}
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={onDragEnd}
      className="list-none"
      drag={canPersist}
    >
      <FabButtonCard
        fab={fab}
        index={index}
        selected={selected}
        onToggle={onToggle}
        isFirst={index === 0}
        isLast={index === rowsLength - 1}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleActive={onToggleActive}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        canPersist={canPersist}
        onDragHandlePointerDown={canPersist ? (e) => dragControls.start(e) : undefined}
      />
    </Reorder.Item>
  );
}

export function FabButtonsPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const fetchData = useFabButtonsFetcher(idPassbook);

  const [rows, setRows] = useState<FabButton[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [listKey, setListKey] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FabButton | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);

  const persistedOrderRef = useRef<string[]>([]);
  const selection = useSelection(rows);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageIndex(0);
    selection.clear();
  }, [debouncedSearch, idPassbook]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchData({ pageIndex, pageSize: PAGE_SIZE, sorting: [{ id: 'order', desc: false }], search: debouncedSearch })
      .then((result) => {
        if (!cancelled) {
          const normalized = normalizeOrderedListItems(result.rows, (f, i) =>
            `${(f.label ?? '').slice(0, 24)}:${(f.value ?? '').slice(0, 24)}:${i}`,
          );
          setRows(normalized);
          setTotal(result.total);
          persistedOrderRef.current = normalized.map((f) => f._id);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageIndex, debouncedSearch, fetchData, idPassbook, listKey]);

  const refresh = useCallback(() => {
    setListKey((k) => k + 1);
    selection.clear();
    void queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] });
  }, [selection, queryClient, idPassbook]);

  const persistDragEnd = useCallback(
    async (id: string) => {
      if (!isPersistedListId(id)) return;
      const before = persistedOrderRef.current;
      const from = before.indexOf(id);
      const to = rows.findIndex((f) => f._id === id);
      if (from === -1 || to === -1 || from === to) return;
      persistedOrderRef.current = rows.map((f) => f._id);
      try {
        await reorderFabButton(idPassbook, id, from, to);
        refresh();
      } catch {
        toast.error('Falha ao reordenar.');
        refresh();
      }
    },
    [rows, idPassbook, refresh],
  );

  const handleToggle = useCallback(
    async (fab: FabButton) => {
      if (!isPersistedListId(fab._id)) {
        toast.error('Este botão não tem id no banco. Reordene um item válido para sincronizar a lista.');
        return;
      }
      try {
        await toggleFabButtonActive(idPassbook, fab._id, !fab.active);
        toast.success(fab.active ? 'Botão desativado.' : 'Botão ativado.');
        refresh();
      } catch {
        toast.error('Falha ao atualizar status.');
      }
    },
    [idPassbook, refresh],
  );

  const handleDelete = useCallback(
    async (fab: FabButton) => {
      if (!isPersistedListId(fab._id)) {
        toast.error('Este botão não tem id no banco e não pode ser excluído por aqui.');
        return;
      }
      const ok = await confirm({
        title: 'Excluir botão',
        description: `Remover permanentemente "${fab.label || 'este botão'}"?`,
        confirmLabel: 'Excluir',
        variant: 'destructive',
      });
      if (!ok) return;
      try {
        await deleteFabButton(idPassbook, fab._id);
        toast.success('Botão excluído.');
        refresh();
      } catch {
        toast.error('Falha ao excluir botão.');
      }
    },
    [confirm, idPassbook, refresh],
  );

  const batchSetActive = useCallback(
    async (active: boolean) => {
      if (selection.selected.length === 0) return;
      setBatchBusy(true);
      let ok = 0;
      let skipped = 0;
      for (const fab of selection.selected) {
        if (!isPersistedListId(fab._id)) {
          skipped += 1;
          continue;
        }
        if (Boolean(fab.active) === active) continue;
        try {
          await toggleFabButtonActive(idPassbook, fab._id, active);
          ok += 1;
        } catch {
          /* continua */
        }
      }
      setBatchBusy(false);
      if (ok > 0) {
        toast.success(`${ok} botão${ok > 1 ? 's' : ''} ${active ? 'ativado' : 'desativado'}${ok > 1 ? 's' : ''}.`);
        refresh();
      } else {
        toast.error('Nenhuma alteração realizada.');
      }
      if (skipped > 0) {
        toast.message(`${skipped} item(ns) sem id ignorado(s). Reordene a lista para sincronizar.`);
      }
    },
    [selection.selected, idPassbook, refresh],
  );

  if (
    !user ||
    !canAccessTenantPath(user.role, `/loja/${idPassbook}/fab-buttons`, passbook, user.email)
  ) {
    return <Navigate to={`/loja/${idPassbook}`} replace />;
  }

  const showSkeleton = isLoading && rows.length === 0;

  return (
    <>
      <PageHeader
        title="Botões flutuantes"
        hideMetaOnMobile
        description={`Ações rápidas exibidas no app — ${passbook.organizationName}`}
        actions={
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="size-4" /> Novo botão
          </Button>
        }
      />

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por rótulo ou conteúdo…"
        listWrapper={(children) => (
          <Reorder.Group as="ul" axis="y" values={rows} onReorder={setRows} className="space-y-2.5 p-0">
            {children}
          </Reorder.Group>
        )}
        selectAllId="select-all-fab"
        itemCount={rows.length}
        allOnPageSelected={selection.allOnPageSelected}
        someOnPageSelected={selection.someOnPageSelected}
        onToggleAllOnPage={selection.toggleAllOnPage}
        isLoading={showSkeleton}
        isEmpty={!isLoading && rows.length === 0}
        emptyTitle="Nenhum botão"
        emptyDescription="Adicione o primeiro botão de ação flutuante da loja."
        footer={
          total > PAGE_SIZE ? (
            <Pagination page={pageIndex} pageSize={PAGE_SIZE} total={total} onPageChange={setPageIndex} className="justify-center pt-2" />
          ) : null
        }
      >
        {rows.map((fab, index) => {
          const canPersist = isPersistedListId(fab._id);
          return (
          <DraggableFabItem
            key={fab._id}
            fab={fab}
            index={index}
            rowsLength={rows.length}
            selected={selection.selectedIds.has(fab._id)}
            onToggle={() => selection.toggle(fab._id)}
            canPersist={canPersist}
            onEdit={() => {
              if (!canPersist) {
                toast.error('Este botão não tem id no banco. Reordene um item válido para sincronizar.');
                return;
              }
              setEditing(fab);
              setFormOpen(true);
            }}
            onDelete={() => handleDelete(fab)}
            onToggleActive={() => handleToggle(fab)}
            onMoveUp={async () => {
              if (!canPersist) return;
              try {
                await moveFabButtonOrder(idPassbook, fab._id, 'up');
                refresh();
              } catch {
                toast.error('Falha ao reordenar.');
              }
            }}
            onMoveDown={async () => {
              if (!canPersist) return;
              try {
                await moveFabButtonOrder(idPassbook, fab._id, 'down');
                refresh();
              } catch {
                toast.error('Falha ao reordenar.');
              }
            }}
            onDragEnd={() => persistDragEnd(fab._id)}
          />
          );
        })}
      </SelectableListLayout>

      <FloatingActionBar count={selection.count} onClear={selection.clear}>
        <Tooltip content="Exibir os botões selecionados no app" side="top">
          <Button size="sm" variant="secondary" disabled={batchBusy} onClick={() => batchSetActive(true)}>
            <Power className="size-4" /> Ativar
          </Button>
        </Tooltip>
        <Tooltip content="Ocultar os botões selecionados no app" side="top">
          <Button size="sm" variant="destructive" disabled={batchBusy} onClick={() => batchSetActive(false)}>
            <Power className="size-4" /> Desativar
          </Button>
        </Tooltip>
      </FloatingActionBar>

      <FabButtonFormDialog
        idPassbook={idPassbook}
        fab={editing}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={refresh}
        allowMgm={
          Boolean(passbook?.hasMemberGetMember) &&
          !rows.some((r) => r.type === 'mgm' && r._id !== editing?._id)
        }
      />
    </>
  );
}
